import React from 'react';
import { StyleSheet, View } from 'react-native';

import {
  ContentfulImageTransformOptions,
  DisplayText,
  FallbackImage,
  IconLink,
  theme,
  useResponsive,
} from '@sg/garnish';

const HomeFooterFallbackImage = require('./home-footer-fallback.png');

// ─────────────────────────────────────────────────────────────────────────────

export const HomeFooter = (props: HomeFooterProps) => {
  const { title, imageUrl, imageA11y, onPress } = props;

  const { match } = useResponsive();
  const minHeight = match([styles.minHeightXS, styles.minHeightSM]);
  const navMargin = match([styles.bottomNavigationMargin, null]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={[styles.container, minHeight, navMargin]}>
      <FallbackImage
        aria-label={imageA11y}
        baseUrl={imageUrl}
        defaultImage={HomeFooterFallbackImage}
        style={[styles.image, minHeight]}
        contentfulOptions={match([CONTENTFUL_OPTIONS_XS, CONTENTFUL_OPTIONS_SM])}
      />

      <DisplayText bold sizeMatch={['32', '48']} style={styles.title}>
        {title}
      </DisplayText>

      <IconLink
        name="IconArrowRight"
        iconSize={24}
        accessibilityLabel={title}
        style={styles.link}
        onPress={onPress}
      />
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const IMAGE_HEIGHT_XS = 110;
const IMAGE_HEIGHT_SM = 330;

const SHARED_CONTENTFUL_OPTIONS: Partial<ContentfulImageTransformOptions> = {
  fm: 'webp',
  fit: 'fill',
  q: 70,
};

const CONTENTFUL_OPTIONS_XS: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  h: IMAGE_HEIGHT_XS,
};

const CONTENTFUL_OPTIONS_SM: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  h: IMAGE_HEIGHT_SM,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    marginTop: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['6'],
  },
  bottomNavigationMargin: {
    marginBottom: theme.spacing['25'],
  },
  minHeightXS: {
    minHeight: IMAGE_HEIGHT_XS,
  },
  minHeightSM: {
    minHeight: IMAGE_HEIGHT_SM,
  },
  image: {
    ...StyleSheet.absoluteFill,
    width: '100%',
  },
  title: {
    color: theme.colors.WHITE,
  },
  link: {
    width: 48,
    height: 48,
    borderRadius: 48,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.WHITE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeFooterProps = {
  title: string;
  imageUrl?: string;
  imageA11y: string;
  onPress: () => void;
};
